// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useMemo, useRef, useState } from "react";

import media from "../../../media";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import HandleSearch from "../../../utils/CommonSearchComponent";
import { store } from "../../../store";

import moment from "moment";
import NavMarker from "../../../components/atoms/NavMarker";
import { VscChevronDown } from "react-icons/vsc";
import { FaBell } from "react-icons/fa";
import useCurrentPath, { decryptData } from "../../../utils/Constants";
import { getLimitedNotificationListThunk } from "../../../store/thunks/dashboardThunk";

import {
  loginAsUserThunk,
  signoutThunk,
  switchBackThunk,
} from "../../../store/thunks/authThunk";
import NotificationPanel from "../../../components/molecules/NotificationPanel/Notification";
import DelegateAccess from "../../DelegateAccess";
import { getDelegateAccesUserDetailThunk } from "../../../store/thunks/delegateAccessThunk";
import { getUserListWithDetailThunk } from "../../../store/thunks/masterDataThunk";
import { message } from "antd";
import CustomLoader from "../../../utils/CustomLoader";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function TopHeader() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = useCurrentPath();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // --------------------------
  // REDUX STATE
  // --------------------------
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);

  const storeCanDelegateAccess = storeUserInfo?.user?.canDelegateAccess;
  const storeHasDelegatedAccess = storeUserInfo?.user?.hasDelegatedAccess;
  const [storeGivenDelegatedAccess, setStoreGivenDelegatedAccess] = useState(
    storeUserInfo?.user?.givenDelegatedAccess ?? false,
  );
  const storeDelegatedAccessUsers = storeUserInfo?.delegations;

  const ncount = useSelector((state) => {
    return state.auth.ncount;
  });
  let isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );
  const delegatedAccessVersion = useSelector(
    (state) => state.delegateAccess.delegatedAccessVersion,
  );

  const isDelegated = useSelector((state) => state.auth.isDelegated);
  const actingUser = useSelector((state) => state.auth.actingUser);
  const originalUser = useSelector((state) => state.auth.originalUser);

  const displayProfilePic = useMemo(() => {
    if (isDelegated) {
      return actingUser.profileImage || null; // When logged in as another user
    }

    if (originalUser?.profileImage) {
      return originalUser.profileImage; // Original user (before delegation)
    }

    return storeUserInfo?.user?.profilePicUrl || null; // Fallback
  }, [isDelegated, actingUser, originalUser, storeUserInfo]);

  const displayUserName = useMemo(() => {
    if (isDelegated) {
      return actingUser.name || null; // When logged in as another user
    }

    if (originalUser?.name) {
      return originalUser.name; // Original user (before delegation)
    }

    return storeUserInfo?.user?.name || "User"; // Fallback
  }, [isDelegated, actingUser, originalUser, storeUserInfo]);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  //Logout Button
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [list, setList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
  });
  const [delegatedAccessUsers, setDelegatedAccessUsers] = useState([]);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [isImgBroken, setIsImgBroken] = useState(false);

  // --------------------------
  // API CALLS
  // --------------------------
  const getNotificationList = () => {
    const payload = {
      lIUId: storeLIUId,
      role: storeRole,
      page: 1,
      pageSize: 5,
      filterValues: "unread",
    };
    dispatch(getLimitedNotificationListThunk(payload))
      .unwrap()
      .then((response) => {
        const temp =
          response &&
          response?.response.map((records, index) => {
            if (records?.isRead === "0") {
              return {
                id: records._id,
                title: records.title,
                excerpt: records.excerpt,
                assignedBy: records.assignedBy,
                assignedTo: records.assignedTo && records.assignedTo,
                message: records?.message,
                type: records?.type,
                isRead: records?.isRead === "0" ? "Un-read" : "Read",
                createdAt: records.createdAt
                  ? moment
                      .utc(records.createdAt)
                      .local()
                      .format("DD-MM-YY hh:mm A")
                  : "",
              };
            }
          });
        setStoreList(temp || []);
      });
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!storeLIUId) return;

      try {
        const res = await dispatch(getUserListWithDetailThunk()).unwrap();
        const userInfo = res.find((u) => u.id === storeLIUId);

        if (userInfo) {
          setStoreGivenDelegatedAccess(userInfo.givenDelegatedAccess ?? false);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, [delegatedAccessVersion, dispatch, storeLIUId]);

  useEffect(() => {
    if (isLoggedIn) {
      // Run the function immediately
      if (storeRole !== process.env.REACT_APP_EROLE) {
        getNotificationList();
      }
      const intervalId = setInterval(() => {
        if (storeRole !== process.env.REACT_APP_EROLE) {
          getNotificationList();
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [storeRole, storeToken, isLoggedIn]);

  //Logout Button
  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [dropdownOpen]);

  const fetchDelegateAccessData = async () => {
    try {
      setShowLoader(true);

      const payload = { fetch: "assignToInfo", assignTo: storeLIUId };
      const delegateRes = await dispatch(
        getDelegateAccesUserDetailThunk(payload),
      ).unwrap();

      const delegateList = Array.isArray(delegateRes)
        ? delegateRes
        : delegateRes
          ? [delegateRes]
          : [];

      if (!delegateList.length) {
        // CLEAR STATE ON REVOKE
        setDelegatedAccessUsers([]);
        return;
      }
      if (delegateList.length) {
        const usersRes = await dispatch(getUserListWithDetailThunk()).unwrap();
        const delegators = delegateList
          .map((d) => usersRes.find((u) => u.id === d.assignBy))
          .filter(Boolean);

        setDelegatedAccessUsers(delegators);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    fetchDelegateAccessData();
  }, [dispatch, storeLIUId, delegatedAccessVersion]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const handleDropdownClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setDropdownOpen(false);
    }
  };

  const openDelegateModal = () => {
    setIsDelegateModalOpen(true);
  };

  const closeDelegateModal = () => {
    setIsDelegateModalOpen(false);
  };

  const handleSearch = async (e) => {
    const searchIn = location.pathname.replace("/", "");

    const callActionFrom = "searchBar";

    const allStates = {
      showLoader: showLoader,
      setShowLoader: setShowLoader,
      pagination: pagination
        ? pagination
        : {
            current: 1,
            page: 1,
            pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
          },
      setPagination: setPagination,
      list: list,
      setList: setList,
    };

    const response = await HandleSearch(
      storeRole,
      storeToken,
      storeLIUId,
      storeAccessTokenExpiresOn,
      searchIn,
      callActionFrom,
      allStates,
      "",
    );
    if (response) {
      // Update local state after fetching data
      const decryptedData = store.getState().auth.list[currentPath + "_info"];

      setStoreList(decryptedData);
      setShowLoader(false);
    }
  };

  const navigateSignIn = () => {
    navigate("/", { replace: true });
  };

  const handleSignOut = async () => {
    try {
      const resultAction = await dispatch(
        signoutThunk({
          role: storeRole,
          lIUId: storeLIUId,
          token: storeToken,
          accessTokenExpiresOn: storeAccessTokenExpiresOn,
        }),
      );

      // Optional: check if the thunk was fulfilled or rejected
      if (signoutThunk.fulfilled.match(resultAction)) {
        console.log("Sign out successful");
      } else {
        console.error("Sign out failed", resultAction.payload);
      }
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };

  const handleSwitchBack = async () => {
    try {
      setShowLoader(true);
      await dispatch(
        switchBackThunk({
          lIUId: originalUser?.lIUId,
          loginAsUserId: actingUser?.lIUId,
        }),
      ).unwrap();
      message.success("Switched back to your account");
      setDropdownOpen(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      message.error(err || "Failed to switch back");
      setDropdownOpen(false);
      setShowLoader(false);
    }
  };

  const handleLoginAs = async (delegatorId) => {
    //if (delegationLoading || isDelegated) return;
    try {
      setShowLoader(true);
      setDelegationLoading(true);
      await dispatch(
        loginAsUserThunk({
          role: storeRole,
          lIUId: storeLIUId,
          token: storeToken,
          accessTokenExpiresOn: storeAccessTokenExpiresOn,
          loginAsUserId: delegatorId,
        }),
      ).unwrap();
      message.success(`Now acting as selected user`);
      navigate("/dashboard");
    } catch (err) {
      message.error(err || "Failed to switch user");
      setDelegationLoading(false);
      setDropdownOpen(false);
      setShowLoader(false);
    }
  };

  const toggleMenu = () => {
    const aside = document.querySelector(".aside");
    if (aside) {
      aside.classList.toggle("open");
    }
  };

  const isDelegationActiveNow = (fromDate, toDate) => {
    if (!fromDate || !toDate) return false;
    const today = moment().format("YYYY-MM-DD");
    const start = moment(fromDate).format("YYYY-MM-DD");
    const end = moment(toDate).format("YYYY-MM-DD");
    return today >= start && today <= end;
  };

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      <CustomLoader showLoader={setShowLoader} />
      <div id="kt_header" className="header header-fixed border-bottom">
        <div className="container-fluid d-flex align-items-center justify-content-between">
          <NavMarker
            route={location.pathname && location.pathname.replace("/", "")}
          />
          <div className="topbar">
            <div className="topbar-col">
              <img
                className="hamburg-icon-start"
                src={media.hamburg.svg}
                alt="menu toggle"
                id="hamburgMobile"
                onClick={toggleMenu}
              />
              <img
                className="hamburg-icon-cross"
                src={media.cross.svg}
                alt="menu toggle"
                id="hamburgMobile"
                onClick={toggleMenu}
              />
            </div>
            <div className="topbar-col">
              <div className="topbar-item header-notification">
                <div
                  className="btn btn-icon btn-clean btn-lg mr-1 position-relative"
                  id="kt_quick_panel_toggle"
                  onClick={() => {
                    document
                      .getElementById("kt_quick_panel")
                      .classList.add("offcanvas-on");
                    document
                      .querySelector("body")
                      .classList.add("overflow-hidden");
                  }}
                >
                  {ncount !== 0 && ncount != null && (
                    <span className="label label-danger ml-2 position-absolute"></span>
                  )}

                  <span className="svg-icon svg-icon-xl svg-icon-primary">
                    <FaBell />
                  </span>
                </div>
              </div>

              <NotificationPanel storeList={storeList} nCount={ncount} />
              <div
                className="dropdow cursor-pointer"
                ref={dropdownRef}
                title={displayUserName}
              >
                <div className="topbar-item" onClick={handleDropdownClick}>
                  <div className="symbol symbol-30 symbol-circle user-profile-dropdown">
                    <img
                      alt="Pic"
                      className="w-100px"
                      src={
                        !isImgBroken && displayProfilePic
                          ? process.env.REACT_APP_BACKEND +
                            "/images/" +
                            displayProfilePic
                          : media.user.png
                      }
                      onError={() => setIsImgBroken(true)}
                    />{" "}
                    <VscChevronDown />
                  </div>
                </div>

                <div
                  className={`borders dropdown-menu p-0 m-0 ${
                    dropdownOpen ? "login-active" : ""
                  }`}
                >
                  <ul className="p-0 mb-0 list-unstyled">
                    {storeHasDelegatedAccess &&
                      storeDelegatedAccessUsers?.length > 0 && (
                        <>
                          {storeDelegatedAccessUsers
                            .filter((user) =>
                              isDelegationActiveNow(user.fromDate, user.toDate),
                            )
                            .map((user) => {
                              const isSameUser = user?.assignBy === storeLIUId;
                              const isActive = isDelegationActiveNow(
                                user.fromDate,
                                user.toDate,
                              );
                              return (
                                <li
                                  key={user.id}
                                  className={`menu-item p-2 px-5 ${
                                    isSameUser ? "disabled text-muted" : ""
                                  }`}
                                  onClick={() =>
                                    !isSameUser &&
                                    handleLoginAs(user?.assignBy?._id)
                                  }
                                >
                                  <span className="font-size-lg font-weight-bolder">
                                    Login as {user?.assignBy?.name}
                                    {isSameUser && " (You)"}
                                  </span>
                                </li>
                              );
                            })}
                        </>
                      )}

                    {storeCanDelegateAccess === true && (
                      <li
                        className="menu-item p-2 px-5"
                        aria-haspopup="true"
                        onClick={openDelegateModal}
                      >
                        <span className="cursor-pointer font-size-lg font-weight-bolder">
                          {storeGivenDelegatedAccess
                            ? "Revoke Access"
                            : "Delegate Access"}
                        </span>
                      </li>
                    )}
                    {isDelegated && originalUser && (
                      <li
                        className="menu-item p-2 px-5 bg-light"
                        aria-haspopup="true"
                        onClick={handleSwitchBack}
                      >
                        <span className="cursor-pointer font-size-lg font-weight-bolder text-danger">
                          Switch back to {originalUser.name}
                        </span>
                      </li>
                    )}
                    <li
                      className="menu-item p-2 px-5"
                      aria-haspopup="true"
                      onClick={handleSignOut}
                    >
                      <span className="cursor-pointer font-size-lg font-weight-bolder">
                        Logout
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DelegateAccess
        isDelegateModalOpen={isDelegateModalOpen}
        setIsDelegateModalOpen={setIsDelegateModalOpen}
        setShowLoader={setShowLoader}
        givenDelegatedAccess={storeGivenDelegatedAccess}
      />
    </>
  );
}
